import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  initialSidebarState,
  isSidebarCollapsed,
  loadSidebarPreference,
  saveSidebarPreference,
  sidebarReducer,
} from '../lib/sidebarState';

interface SidebarContextValue {
  /** Whether the desktop sidebar is the narrow icon rail. */
  collapsed: boolean;
  toggle: () => void;
  /**
   * What the last mounted desktop rail showed (null before the first one).
   * Every page renders its own Layout, so a page change mounts a new rail;
   * it starts from this value and glides to `collapsed` instead of jumping.
   */
  shownRef: MutableRefObject<boolean | null>;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

/**
 * Lives inside the router but above the routes: the state survives page
 * changes (Layout re-mounts per page) and follows the URL for the
 * auto-collapse on Verläufe.
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [state, dispatch] = useReducer(sidebarReducer, pathname, (path) =>
    initialSidebarState(path, loadSidebarPreference()),
  );

  // Follow the URL while rendering, not in an effect: the new page's first
  // frame already has the right state (no flash of the old one).
  if (sidebarReducer(state, { type: 'navigate', pathname }) !== state) {
    dispatch({ type: 'navigate', pathname });
  }

  // Persist only real changes of the preference, never the value just loaded.
  const persisted = useRef(state.preference);
  useEffect(() => {
    if (state.preference === persisted.current) return;
    persisted.current = state.preference;
    saveSidebarPreference(state.preference);
  }, [state.preference]);

  const toggle = useCallback(() => dispatch({ type: 'toggle' }), []);
  const shownRef = useRef<boolean | null>(null);
  const collapsed = isSidebarCollapsed(state);

  const value = useMemo(() => ({ collapsed, toggle, shownRef }), [collapsed, toggle]);
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
